import { Header } from "@/components/Header";
import { Helmet } from "react-helmet-async";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Use - MacroVault</title>
        <meta name="description" content="Read MacroVault's Terms of Use and understand the rules for using our VBA macro platform." />
        <link rel="canonical" href="https://findmacros.com/terms" />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-6">Terms of Use</h1>
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-muted-foreground mb-4">Last updated: November 2025</p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Welcome to MacroVault</h2>
          <p>
            These Terms of Use (“Terms”) govern your access to and use of MacroVault, a service provided by MacroFlow
            LLC (“we,” “us,” “our”). By creating an account or using the Service, you agree to these Terms and our
            Privacy Notice. If you do not agree, do not use the Service.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Our Service</h2>
          <p>
            MacroVault is a cloud platform for storing, managing, and sharing VBA macros. Features may change over time;
            we may modify, suspend, or discontinue parts of the Service without notice. We aim for reliability but
            cannot guarantee uninterrupted availability.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">User Accounts</h2>
          <p>
            You must create and verify an account to use MacroVault. You are responsible for your credentials and all
            activity under your account. If you suspect unauthorized use, contact us at{" "}
            <a href="mailto:legal@macroflow.ai">legal@macroflow.ai</a>.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">User Content & Ownership</h2>
          <p>
            You retain ownership of VBA macros, code, comments, and other materials you upload or create on MacroVault
            (“User Content”). You represent that you have the rights to upload and share your User Content.
          </p>
          <p>
            <strong>License to MacroFlow LLC.</strong> You grant us a worldwide, non-exclusive, royalty-free license to
            host, store, display, perform, and otherwise use your User Content as needed to operate and improve the
            Service (including to display public content, process backups, and enable search and discovery).
          </p>
          <p>
            <strong>Public vs. Private Content.</strong> Private content is accessible only to you and those you
            explicitly share it with (if such sharing is enabled). Public content is viewable and downloadable by others
            and may be indexed or featured within the Service.
          </p>
          <p>
            <strong>License to Other Users (GitHub-style).</strong> For <em>public</em> content, you grant other users a
            limited license to view and download your content
            <em>within MacroVault</em> for the Service’s intended functionality. Reuse or distribution <em>outside</em>{" "}
            MacroVault is governed by the license you attach to your macro.{" "}
            <strong>
              If you do not attach an open-source or other license, no additional rights are granted for use outside
              MacroVault.
            </strong>
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Acceptable Use</h2>
          <ul>
            <li>No illegal, harmful, or malicious code or content.</li>
            <li>No infringement of intellectual property, privacy, or other rights.</li>
            <li>No attempts to breach security, scrape at scale, or abuse the Service.</li>
            <li>No misrepresentation of identity or affiliation.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Payment & Future Features</h2>
          <p>
            Some features may become paid in the future (e.g., premium storage, marketplace, pro accounts). We will
            provide clear notice before charging. We do not collect payment details at this time.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Intellectual Property (Site Content)</h2>
          <p>
            Except for User Content, the Service and its content—including the MacroVault and MacroFlow names, logos,
            UI, and designs—are owned by MacroFlow LLC and protected by copyright, trademark, and other laws. You may
            not copy, modify, or create derivative works of the Service or our brand assets without written permission.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Copyright Notice</h2>
          <p>
            © 2025 MacroFlow LLC. All rights reserved. Unauthorized reproduction or distribution of the Service’s
            proprietary content is prohibited except as expressly permitted in these Terms or with our written consent.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">DMCA & Copyright Complaints</h2>
          <p>
            If you believe content on MacroVault infringes your copyright, email{" "}
            <a href="mailto:legal@macroflow.ai">legal@macroflow.ai</a> with:
          </p>
          <ul>
            <li>Your name, address, email, and phone number;</li>
            <li>A description of the copyrighted work you claim is infringed;</li>
            <li>The URL or exact location of the allegedly infringing content on MacroVault;</li>
            <li>
              A statement of good-faith belief that the use is not authorized by the copyright owner, its agent, or the
              law;
            </li>
            <li>
              A statement, under penalty of perjury, that the information is accurate and that you are the owner or
              authorized to act; and
            </li>
            <li>Your physical or electronic signature.</li>
          </ul>
          <p>
            Upon valid notice, we may remove or restrict access to the content and notify the user. Repeat infringers
            may be suspended or terminated.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Termination</h2>
          <p>
            You may delete your account at any time. We may suspend or terminate accounts that violate these Terms or
            pose legal, security, or reputational risks. Some information (e.g., audit logs) may be retained for a
            limited period for legitimate business or legal purposes.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Disclaimers</h2>
          <p>
            MacroVault is provided “as is” and “as available.” We disclaim all warranties, express or implied, including
            merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service
            will be error-free or uninterrupted.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, MacroFlow LLC and our affiliates will not be liable for any
            indirect, incidental, special, consequential, or exemplary damages, or any loss of data, profits, or
            business, arising from or related to your use of the Service.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Governing Law & Venue</h2>
          <p>
            These Terms are governed by the laws of the State of New York, USA, without regard to conflicts principles.
            You agree to the exclusive jurisdiction and venue of the state and federal courts located in New York
            County, New York.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Changes to These Terms</h2>
          <p>
            We may update these Terms to reflect new features, legal requirements, or changes in our practices. If
            updates are material, we will notify you via email or in-app notice. The “Last updated” date shows the most
            recent version.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h2>
          <p>Questions or legal notices:</p>
          <p className="mb-0">
            <strong>MacroFlow LLC</strong>
            <br />
            Attn: Legal
            <br />
            New York, USA
            <br />
            <a href="mailto:legal@macroflow.ai">legal@macroflow.ai</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Terms;
